import React from 'react';
import { useAccountStore } from '../../components/Stores/accountStore';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';
import { useSupplyPositions } from '../../hooks/useSupplyPositions';
import { IInventoryReportType } from '../../sp-types/InventoryReportType';
import { useFormulasPrices } from '@hooks/useFormulasPrices';
import { useFx } from '@hooks/useFx';
import { useBaseCurrency } from '@hooks/useBaseCurrency';
import { aggregateSupplyByMaterial, aggregateSupplyPositionsToMaterialsDic } from './supplyUtils';
import { splitByHorizon, getUniqueFormulaCodes, filterByFormula } from '../../utils/positionUtils';
import { EmptyInventorySummery } from './inventory-components/EmptyInventorySummery';
import { SupplyGrid } from './inventory-components/TransactionGrid/SupplyGrid';
import { getUnitObj } from '../../utils/MassUnits/massUnitsUtils';
import SummaryGraphs from './inventory-components/SummaryGraphs';
import { SupplySummaryGraphs } from './SupplySummaryGraphs';
import { StatusType } from '../../sp-types/statusType';
import { UnderWritingTransactionGrid } from './inventory-components/TransactionGrid/UnderWritingTransactionGrid';
import { useUser } from '@hooks/useUser';
import { useCardBar } from '../../components/FormulaCardBar/useCardBar';
import { GENERAL_ID, useExposureStore } from '../../components/Stores/exposureDataStore';
import { ExposureHorizonTableBanner } from '../../components/ExposureHorizonTableBanner';
import { getExposureWindow } from '../../utils/exposureHorizon';
import { getSupplyTerm } from '../../utils/categoryTerms';

export default function Inventory() {
  const { account, formulaColorDictionary: colorsMap } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const {
    currentSelectedFormulaId: materialId,
    setCurrentSelectedFormulaId: setMaterialId,
  } = useExposureStore();

  const { data: user } = useUser();
  
  // Use new Postgres API for supply positions
  const { data: supplyPositions, isLoading: isSupplyLoading, refetch: refetchSupply } = useSupplyPositions();
  const { data: pricesData } = useFormulasPrices();
  const spotPricesMap = pricesData?.pricesMap || {} as Record<string, { price: number; currency: string }>;
  const priceAsOfDate = pricesData?.asOfDate || null;
  const baseCurrency = useBaseCurrency();
  const { getRateToBase } = useFx(baseCurrency);
  
  // Aggregate supply by material for summary cards
  const supplyAggregated = React.useMemo(() => {
    if (!supplyPositions || supplyPositions.length === 0) return {};
    
    // Convert pricesMap to simple price dictionary (already in base currency)
    const priceDic: Record<string, number> = {};
    Object.entries(spotPricesMap).forEach(([formulaCode, priceInfo]) => {
      priceDic[formulaCode] = priceInfo.price;
    });
    
    // When 'general' is selected, pass undefined to aggregate all materials
    return aggregateSupplyByMaterial(
      supplyPositions,
      priceDic,
      getRateToBase
    );
  }, [supplyPositions, spotPricesMap, getRateToBase, materialId]);
  
  console.log('[Inventory] Postgres supply positions:', supplyPositions?.length || 0);
  console.log('[Inventory] Aggregated by material:', Object.keys(supplyAggregated));

  const [showOutOfScope, setShowOutOfScope] = React.useState(false);

  // Initialize to 'general' mode if no materialId is set
  React.useEffect(() => {
    if (!materialId) {
      setMaterialId('general');
    }
  }, [materialId, setMaterialId]);

  // Calculate viewing horizon and filter positions
  const horizonMonths = account?.viewingHorizonMonths || 12;
  const exposureWindow = React.useMemo(() => getExposureWindow(horizonMonths), [horizonMonths]);

  const { inScopePositions, outOfScopePositions } = React.useMemo(() => {
    if (!supplyPositions) return { inScopePositions: [], outOfScopePositions: [] };
    const { inScope, outOfScope } = splitByHorizon(supplyPositions, exposureWindow);
    return { inScopePositions: inScope, outOfScopePositions: outOfScope };
  }, [supplyPositions, exposureWindow]);

  // Positions to display based on toggle
  // showOutOfScope=false: show only in-scope
  // showOutOfScope=true: show all (including out-of-scope)
  const displayPositions = React.useMemo(() => {
    return showOutOfScope ? supplyPositions : inScopePositions;
  }, [showOutOfScope, supplyPositions, inScopePositions]);

  // Aggregate supply positions for charts (with exposureCalendar)
  const supplyAggregatedForCharts = React.useMemo(() => {
    if (!displayPositions || displayPositions.length === 0) return {};
    
    // Convert pricesMap to simple price dictionary
    const priceDic: Record<string, number> = {};
    Object.entries(spotPricesMap).forEach(([formulaCode, priceInfo]) => {
      priceDic[formulaCode] = priceInfo.price;
    });
    
    // Exclude ratio POs from charts — consistent with table summary
    const nonRatioPositions = displayPositions.filter(p => p.supply_type !== 'ratio_po');
    
    return aggregateSupplyPositionsToMaterialsDic(
      nonRatioPositions,
      materialId && materialId !== 'general' ? materialId : undefined,
      priceDic,
      getRateToBase,
    );
  }, [displayPositions, materialId, spotPricesMap, getRateToBase]);

  // Filter supply positions by selected material
  const filteredSupplyPositions = React.useMemo(() => {
    if (!supplyPositions) return [];
    if (materialId === 'general') return supplyPositions;
    if (!materialId) return [];
    return filterByFormula(supplyPositions, materialId);
  }, [supplyPositions, materialId]);
  
  const isSupplyEmpty = (): boolean => {
    return !filteredSupplyPositions || filteredSupplyPositions.length === 0 || 
           Object.keys(supplyAggregated).length === 0;
  };
  
  const [dataStatus, setDataStatus] = React.useState<'loading' | 'missing' | 'fetched'>('missing');
  
  React.useEffect(() => {
    if (isSupplyLoading) {
      setDataStatus('loading');
    } else if (supplyPositions && supplyPositions.length > 0) {
      setDataStatus('fetched');
    } else {
      setDataStatus('missing');
    }
  }, [isSupplyLoading, supplyPositions]);
  // Get unique formula codes from supply positions
  const allSupplyMaterials = supplyPositions 
    ? getUniqueFormulaCodes(supplyPositions)
    : [];

  // Configure shared formula card bar
  useCardBar({ filterFormulaCodes: allSupplyMaterials });

  return (
    <div className="flex flex-col h-full space-y-[1rem]">
      <ExposureHorizonTableBanner
        window={exposureWindow}
        inScopeCount={inScopePositions.length}
        outOfScopeCount={outOfScopePositions.length}
        totalCount={supplyPositions?.length || 0}
        showOutOfScope={showOutOfScope}
        onToggleOutOfScope={setShowOutOfScope}
        categoryName={getSupplyTerm(account)}
      />
      <div>
        <SupplySummaryGraphs
          componentDataState={dataStatus}
          supplyAggregated={supplyAggregatedForCharts}
        />
      </div>
      <div className="flex flex-col flex-1 grow">
        {isSupplyEmpty() ? (
          <EmptyInventorySummery />
        ) : (
          <SupplyGrid
            supplyPositions={displayPositions || []}
            colorDictionary={colorsMap}
            materialsMap={materialsMap}
            pricesMap={spotPricesMap}
            priceAsOfDate={priceAsOfDate}
            unit={materialsMap[materialId]?.unit ? (getUnitObj(materialsMap[materialId].unit)?.shortForm || 'MT') : 'MT'}
            materialId={materialId}
            exposureWindow={exposureWindow}
            formulaName={account.formulasToAccount.find(f => f.formulaId === materialId)?.displayName || materialId}
          />
        )}
      </div>
    </div>
  );
}

const isInventoryEmpty = (inventoryReports: IInventoryReportType[] | undefined | null): boolean => {
  if (!inventoryReports) return false;
  return !inventoryReports.some((item) => item.quantity > 0);
};
